import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { value: '7d', label_hi: '7 दिन', label_en: '7d' },
  { value: '30d', label_hi: '30 दिन', label_en: '30d' },
  { value: '90d', label_hi: '90 दिन', label_en: '90d' },
  { value: '365d', label_hi: '1 साल', label_en: '1y' },
];

const SCHOOL_TYPES = [
  { value: 'all', label_hi: 'सभी', label_en: 'All' },
  { value: 'demo', label_hi: 'Demo', label_en: 'Demo' },
  { value: 'paid', label_hi: 'Paid', label_en: 'Paid' },
];

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
        active
          ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
          : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
      }`}
    >
      {children}
    </button>
  );
}

function KPICard({ label, value, delta, icon, testid }) {
  const positive = delta != null && delta >= 0;
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        </div>
        <div className="text-2xl">{icon}</div>
      </div>
      {delta != null && (
        <div className={`mt-2 text-xs font-semibold inline-flex items-center gap-1 ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
          {positive ? '↑' : '↓'} {Math.abs(delta)}%
          <span className="text-slate-400 font-normal ml-1">vs prev</span>
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, children, action, testid }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-slate-800 text-sm">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function fmtINR(n) {
  if (n == null) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

// =================== PLATFORM MODE ===================

function PlatformAnalytics({ token }) {
  const { language } = useLanguage();
  const t = (hi, en) => (language === 'hi' ? hi : en);
  const [range, setRange] = useState('30d');
  const [schoolType, setSchoolType] = useState('all');
  const [overview, setOverview] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const exportReport = useCallback(async (format) => {
    setExporting(true);
    try {
      const r = await fetch(`${API_URL}/api/analytics-v2/platform/export?range=${range}&school_type=${schoolType}&format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { setExporting(false); return; }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `board_report.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('export error', e); }
    finally { setExporting(false); }
  }, [token, range, schoolType]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [ov, br, fn] = await Promise.all([
        fetch(`${API_URL}/api/analytics-v2/platform/overview?range=${range}&school_type=${schoolType}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/analytics-v2/platform/breakdown`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/analytics-v2/platform/funnel?range=${range}`, { headers }).then(r => r.json()),
      ]);
      setOverview(ov);
      setBreakdown(br);
      setFunnel(fn);
    } catch (e) {
      console.error('Analytics load error', e);
    } finally {
      setLoading(false);
    }
  }, [token, range, schoolType]);

  useEffect(() => { load(); }, [load]);

  const maxFunnel = useMemo(
    () => (funnel?.funnel || []).reduce((m, s) => Math.max(m, s.count || 0), 1),
    [funnel]
  );

  return (
    <div className="space-y-6" data-testid="rich-analytics-panel-platform">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('अवधि', 'Range')}</span>
          <div className="flex gap-1.5" data-testid="analytics-range-filter">
            {RANGES.map(r => (
              <FilterPill key={r.value} active={range === r.value} onClick={() => setRange(r.value)} testid={`range-${r.value}`}>
                {language === 'hi' ? r.label_hi : r.label_en}
              </FilterPill>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('स्कूल प्रकार', 'School Type')}</span>
          <div className="flex gap-1.5" data-testid="analytics-school-type-filter">
            {SCHOOL_TYPES.map(s => (
              <FilterPill key={s.value} active={schoolType === s.value} onClick={() => setSchoolType(s.value)} testid={`school-type-${s.value}`}>
                {language === 'hi' ? s.label_hi : s.label_en}
              </FilterPill>
            ))}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => exportReport('pdf')} disabled={exporting || loading} className="px-3 py-1.5 text-xs font-medium bg-rose-600 hover:bg-rose-700 text-white rounded-lg disabled:opacity-50" data-testid="analytics-export-pdf">
            <i className="fa-solid fa-file-pdf mr-1"></i>PDF
          </button>
          <button onClick={() => exportReport('xlsx')} disabled={exporting || loading} className="px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50" data-testid="analytics-export-excel">
            <i className="fa-solid fa-file-excel mr-1"></i>Excel
          </button>
          <button onClick={load} className="px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg" data-testid="analytics-refresh">
            <i className="fa-solid fa-rotate mr-1"></i>{t('रिफ्रेश', 'Refresh')}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          testid="kpi-revenue"
          label={t('कुल राजस्व', 'Total Revenue')}
          value={fmtINR(overview?.kpis?.total_revenue)}
          delta={overview?.kpis?.revenue_delta_pct}
          icon="💰"
        />
        <KPICard
          testid="kpi-signups"
          label={t('नए स्कूल', 'New Schools')}
          value={overview?.kpis?.total_signups ?? 0}
          delta={overview?.kpis?.signups_delta_pct}
          icon="🏫"
        />
        <KPICard
          testid="kpi-payments"
          label={t('कुल Payments', 'Paid Payments')}
          value={overview?.kpis?.paid_payments ?? 0}
          icon="✅"
        />
        <KPICard
          testid="kpi-mrr"
          label={t('MRR (30d)', 'MRR (30d)')}
          value={fmtINR(overview?.kpis?.mrr_estimate)}
          icon="📈"
        />
      </div>

      {/* Revenue + Signups Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard testid="chart-revenue-trend" title={t('राजस्व ट्रेंड', 'Revenue Trend')}>
          {overview?.series?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={overview.series}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtINR} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">{loading ? t('लोड हो रहा है...', 'Loading...') : t('कोई डेटा नहीं', 'No data')}</div>
          )}
        </ChartCard>

        <ChartCard testid="chart-signups" title={t('स्कूल Signups', 'School Signups')}>
          {overview?.series?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={overview.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="signups" fill="#f97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">{t('कोई डेटा नहीं', 'No data')}</div>
          )}
        </ChartCard>
      </div>

      {/* Funnel + Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard testid="chart-funnel" title={t('Conversion Funnel', 'Conversion Funnel')}>
          <div className="space-y-3" data-testid="funnel-stages">
            {(funnel?.funnel || []).map((stage, i) => (
              <div key={stage.stage}>
                <div className="flex justify-between text-xs font-medium text-slate-700">
                  <span>{stage.stage}</span>
                  <span>
                    {stage.count}
                    {stage.conversion_pct != null && (
                      <span className="ml-2 text-slate-400">({stage.conversion_pct}%)</span>
                    )}
                  </span>
                </div>
                <div className="mt-1 h-7 bg-slate-100 rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md flex items-center justify-end px-2 text-white text-xs font-bold transition-all"
                    style={{
                      width: `${Math.max(8, (stage.count / maxFunnel) * 100)}%`,
                      backgroundColor: stage.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          {funnel?.overall_conversion_pct != null && (
            <p className="mt-4 text-xs text-slate-600 bg-slate-50 rounded-lg p-2">
              {t('कुल conversion दर', 'Overall conversion')}: <span className="font-bold text-emerald-600">{funnel.overall_conversion_pct}%</span>
            </p>
          )}
        </ChartCard>

        <ChartCard testid="chart-distribution" title={t('स्कूल वितरण', 'School Distribution')}>
          {breakdown?.school_distribution?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={breakdown.school_distribution}
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {breakdown.school_distribution.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-slate-400 text-sm">{t('कोई डेटा नहीं', 'No data')}</div>
          )}
        </ChartCard>

        <ChartCard testid="chart-top-schools" title={t('Top Revenue Schools', 'Top Revenue Schools')}>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(breakdown?.top_schools_by_revenue || []).map((s, i) => (
              <div key={s.school_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                <span className="text-xs font-bold text-slate-400 w-5">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{s.name}</p>
                  {s.is_demo && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">DEMO</span>}
                </div>
                <span className="text-sm font-semibold text-emerald-600">{fmtINR(s.revenue)}</span>
              </div>
            ))}
            {!breakdown?.top_schools_by_revenue?.length && (
              <div className="text-center py-8 text-slate-400 text-sm">{t('कोई डेटा नहीं', 'No data')}</div>
            )}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

// =================== SCHOOL MODE ===================

function SchoolAnalytics({ token }) {
  const { language } = useLanguage();
  const t = (hi, en) => (language === 'hi' ? hi : en);
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics-v2/school/overview?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      setData(j);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="rich-analytics-panel-school">
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center gap-4">
        <h3 className="font-bold text-slate-800">📊 {t('विस्तृत विश्लेषण', 'Detailed Analytics')}</h3>
        <div className="flex gap-1.5" data-testid="school-analytics-range">
          {RANGES.map(r => (
            <FilterPill key={r.value} active={range === r.value} onClick={() => setRange(r.value)} testid={`s-range-${r.value}`}>
              {language === 'hi' ? r.label_hi : r.label_en}
            </FilterPill>
          ))}
        </div>
        <div className="ml-auto">
          <button onClick={load} className="px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg" data-testid="school-analytics-refresh">
            <i className="fa-solid fa-rotate mr-1"></i>{t('रिफ्रेश', 'Refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard testid="s-kpi-students" label={t('कुल छात्र', 'Total Students')} value={data?.kpis?.total_students ?? 0} icon="👨‍🎓" />
        <KPICard testid="s-kpi-teachers" label={t('शिक्षक', 'Teachers')} value={data?.kpis?.total_teachers ?? 0} icon="👩‍🏫" />
        <KPICard testid="s-kpi-collected" label={t('Fee संग्रह', 'Fees Collected')} value={fmtINR(data?.kpis?.total_collected)} icon="💰" />
        <KPICard testid="s-kpi-attendance" label={t('औसत उपस्थिति', 'Avg Attendance')} value={`${data?.kpis?.avg_attendance_pct ?? 0}%`} icon="✅" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard testid="s-chart-attendance" title={t('दैनिक उपस्थिति %', 'Daily Attendance %')}>
          {data?.attendance_series?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.attendance_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Line type="monotone" dataKey="attendance_pct" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3, fill: '#3b82f6' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-slate-400 text-sm">{loading ? t('लोड हो रहा है...', 'Loading...') : t('कोई डेटा नहीं', 'No data')}</div>
          )}
        </ChartCard>

        <ChartCard testid="s-chart-fees" title={t('दैनिक Fee संग्रह', 'Daily Fee Collection')}>
          {data?.fee_series?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.fee_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtINR} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="amount" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-60 flex items-center justify-center text-slate-400 text-sm">{t('कोई डेटा नहीं', 'No data')}</div>
          )}
        </ChartCard>
      </div>

      <ChartCard testid="s-chart-class-heat" title={t('कक्षा-वार उपस्थिति (पिछले 7 दिन)', 'Class-wise Attendance (Last 7 days)')}>
        {data?.class_attendance_heat?.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.class_attendance_heat.map(c => {
              const pct = c.attendance_pct;
              const color = pct >= 90 ? 'bg-emerald-500' : pct >= 80 ? 'bg-lime-500' : pct >= 70 ? 'bg-amber-500' : 'bg-rose-500';
              return (
                <div key={c.class} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-semibold text-slate-700">{c.class}</span>
                    <span className="text-base font-bold text-slate-900">{pct}%</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`${color} h-full rounded-full`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">{c.samples} {t('records', 'records')}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-slate-400 text-sm">{t('कोई डेटा नहीं', 'No data')}</div>
        )}
      </ChartCard>
    </div>
  );
}

// =================== EXPORT ===================

export default function RichAnalyticsPanel({ token, mode = 'platform' }) {
  if (mode === 'school') return <SchoolAnalytics token={token} />;
  return <PlatformAnalytics token={token} />;
}
